import { createDraftSafeSelector } from '@reduxjs/toolkit';

import { RootState } from '@app/redux/interface';

export const getAuthState = (state: RootState) => state.auth;

export const getIsLoggedIn = createDraftSafeSelector(getAuthState, authState => authState.isLoggedIn);

export const getCurrentUser = createDraftSafeSelector(getAuthState, authState => authState.currentUser);

export const isVerificationAccount = createDraftSafeSelector(getAuthState, authState => {
	const isVerificationPhoneRequired = Boolean(authState.currentUser?.phone_verification_needed);
	const isVerificationEmailRequired = Boolean(authState.currentUser?.email_verification_needed);
	const shouldEnableMfa = Boolean(authState.currentUser?.should_enable_mfa);
	const isMfaEnabled = Boolean(authState.currentUser?.mfa_enabled);
	const isPhoneVerified = Boolean(authState.currentUser?.phone_verified);
	const isEmailVerified = Boolean(authState.currentUser?.email_verified);
	const isPhoneChecked = (isVerificationPhoneRequired && isPhoneVerified) || !isVerificationPhoneRequired;
	const isEmailChecked = (isVerificationEmailRequired && isEmailVerified) || !isVerificationEmailRequired;
	const isMfaChecked = (shouldEnableMfa && isMfaEnabled) || !shouldEnableMfa;

	return {
		needVerifyAccount:
			(isVerificationPhoneRequired && !isPhoneVerified) || (isVerificationEmailRequired && !isEmailVerified),
		isVerified: isPhoneChecked && isEmailChecked && isMfaChecked,
		needMFASetup: shouldEnableMfa && !isMfaEnabled,
		isVerificationPhoneRequired,
		isPhoneVerified,
		isVerificationEmailRequired,
		isEmailVerified,
	};
});

export const getResetPasswordDetails = createDraftSafeSelector(getAuthState, authState => authState.resetPassword);

export const getVerificationEmailCodeExpiring = createDraftSafeSelector(
	getAuthState,
	authState => authState.verifyEmail.expires_at,
);

export const getVerificationPhoneCodeExpiring = createDraftSafeSelector(
	getAuthState,
	authState => authState.verifyPhone.expires_at,
);

export const getVerificationPhoneType = createDraftSafeSelector(getAuthState, authState => authState.verifyPhone.type);

export const getAccessTokenExpiresAt = createDraftSafeSelector(
	getAuthState,
	authState => authState.tokens?.access_expires_at,
);

export const getAccessToken = createDraftSafeSelector(getAuthState, authState => authState.tokens?.access_token);

export const getRefreshToken = createDraftSafeSelector(getAuthState, authState => authState.tokens?.refresh_token);
